import type { Dispatch } from 'react'

import type { ThreadAction } from 'src/components/Threads/V2/Provider/ThreadContext'
import { logger } from 'src/lib/logger'

interface StreamState {
  final_tool_calls: Record<string, any>
  content: string
}

const onDone = (
  controller: ReadableStreamDefaultController<Uint8Array>,
  state: StreamState,
  dispatch: Dispatch<ThreadAction>,
  encoder: TextEncoder
) => {
  // Process completed tool calls - dispatch ready events
  const toolCalls = Object.values(state.final_tool_calls).map((toolCall) => ({
    ...toolCall,
    function: {
      ...toolCall.function,
      arguments: JSON.parse(toolCall.function.arguments),
    },
  }))

  // Debug: Log all completed tool calls
  if (toolCalls.length > 0) {
    logger.info('SSE processor: Completed tool calls', {
      toolCallsCount: toolCalls.length,
      toolCallIds: toolCalls.map((tc) => ({
        name: tc.function?.name,
        id: tc.id,
        toolUuid: tc.toolUuid,
      })),
    })

    dispatch({
      type: 'TOOL_DETAILS_RECEIVED',
      payload: {
        toolCalls: toolCalls || [],
      },
    })
  }

  // Set pending tool calls for keyword_search (client-side)
  const keywordSearchCalls = toolCalls.filter(
    (tc) => tc.function?.name === 'keyword_search'
  )

  // Set pending tool calls for server-side tools (everything else)
  const serverSideToolCalls = toolCalls.filter(
    (tc) => tc.function?.name !== 'keyword_search'
  )

  // Always set pending tool calls for keyword_search, regardless of content
  if (keywordSearchCalls.length > 0) {
    // Debug: Log keyword search tool calls
    logger.info('SSE processor: Setting pending keyword search calls', {
      keywordSearchCount: keywordSearchCalls.length,
      keywordSearchIds: keywordSearchCalls.map((tc) => ({
        id: tc.id,
        toolUuid: tc.toolUuid,
      })),
      hasContent: !!state.content,
    })

    dispatch({
      type: 'SET_PENDING_TOOL_CALLS',
      payload: {
        toolCalls: keywordSearchCalls,
      },
    })
  }

  // Set pending server-side tool calls
  if (serverSideToolCalls.length > 0) {
    logger.info('SSE processor: Setting pending server-side tool calls', {
      serverSideCount: serverSideToolCalls.length,
      serverSideIds: serverSideToolCalls.map((tc) => ({
        name: tc.function?.name,
        id: tc.id,
        toolUuid: tc.toolUuid,
      })),
      hasContent: !!state.content,
    })

    dispatch({
      type: 'SET_PENDING_SERVER_TOOL_CALLS',
      payload: {
        toolCalls: serverSideToolCalls,
      },
    })
  }

  if (!state.content) {
    dispatch({
      type: 'STREAM_ENDED',
      payload: { html: '', json: [] },
    })
    controller.enqueue(encoder.encode('<p></p>'))
  }
}

export function processSSEStream(
  stream: ReadableStream<Uint8Array>,
  dispatch?: Dispatch<ThreadAction>
): ReadableStream<Uint8Array> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  const encoder = new TextEncoder()
  let buffer = ''
  const state: StreamState = {
    final_tool_calls: {},
    content: '',
  }

  return new ReadableStream<Uint8Array>({
    async start(controller) {
      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            processBuffer({
              buffer,
              controller,
              encoder,
              state,
              streamEnding: true,
              dispatch,
            })
            if (dispatch) {
              onDone(controller, state, dispatch, encoder)
            }
            break
          }

          buffer += decoder.decode(value, { stream: true })
          buffer = processBuffer({
            buffer,
            controller,
            encoder,
            state,
            streamEnding: false,
            dispatch,
          })
        }
      } catch (error) {
        logger.warn('Error reading or processing SSE stream:', error)
        controller.close()
      } finally {
        controller.close()
      }
    },
    cancel() {
      reader.cancel()
      logger.dev('SSE stream cancelled')
    },
  })
}

function processBuffer({
  buffer,
  controller,
  encoder,
  state,
  streamEnding = false,
  dispatch,
}: {
  buffer: string
  controller: ReadableStreamDefaultController<Uint8Array>
  encoder: TextEncoder
  state: StreamState
  streamEnding?: boolean
  dispatch?: Dispatch<ThreadAction>
}): string {
  let position = 0
  while (position < buffer.length) {
    const newlineIndex = buffer.indexOf('\n\n', position)
    if (newlineIndex === -1) {
      if (streamEnding && buffer.length > position) {
        // Attempt to process the final chunk
        processMessageBlock({
          messageBlock: buffer.substring(position),
          controller,
          encoder,
          state,
          dispatch,
        })
        position = buffer.length
      } else {
        // Need more data
        break
      }
    } else {
      const messageBlock = buffer.substring(position, newlineIndex)
      position = newlineIndex + 2 // Move past the '\n\n'
      processMessageBlock({
        messageBlock,
        controller,
        encoder,
        state,
        dispatch,
      })
    }
  }
  return buffer.substring(position)
}

const ensureHtmlTagStart = (input: string): string => {
  const trimmedInput = input.trimStart()
  if (trimmedInput.startsWith('<')) {
    return trimmedInput
  } else {
    return `<p>${trimmedInput}`
  }
}

function processMessageBlock({
  messageBlock,
  controller,
  encoder,
  state,
  dispatch,
}: {
  messageBlock: string
  controller: ReadableStreamDefaultController<Uint8Array>
  encoder: TextEncoder
  state: StreamState
  dispatch?: Dispatch<ThreadAction>
}) {
  const lines = messageBlock.split('\n')
  for (const line of lines) {
    if (line.startsWith('data: ')) {
      const jsonData = line.substring(5).trim()
      if (jsonData) {
        try {
          const chunk = JSON.parse(jsonData)

          // Handle tool call failure events
          if (chunk.type === 'tool_call_failed') {
            if (dispatch) {
              dispatch({
                type: 'TOOL_CALL_FAILED',
                payload: {
                  toolUuid: chunk.toolUuid,
                  toolName: chunk.toolName,
                  error: chunk.error,
                },
              })
            }
            return
          }

          // Handle model change events
          if (chunk.type === 'model_changed') {
            if (dispatch) {
              dispatch({
                type: 'MODEL_CHANGED',
                payload: {
                  oldModelId: chunk.oldModelId,
                  newModelId: chunk.newModelId,
                  reason: chunk.reason,
                },
              })
            }
            return
          }

          // Safety check for malformed chunks
          if (!chunk.choices || !chunk.choices[0] || !chunk.choices[0].delta) {
            logger.warn('Malformed SSE chunk received', {
              chunk,
              hasChoices: !!chunk.choices,
              choicesLength: chunk.choices?.length,
              hasFirstChoice: !!chunk.choices?.[0],
              hasDelta: !!chunk.choices?.[0]?.delta,
            })
            return
          }

          const currentDelta = chunk.choices[0].delta

          if (currentDelta.content) {
            if (state.content === '') {
              controller.enqueue(
                encoder.encode(ensureHtmlTagStart(currentDelta.content))
              )
            } else {
              controller.enqueue(encoder.encode(currentDelta.content))
            }
            state.content += currentDelta.content
          } else if (currentDelta.tool_calls) {
            currentDelta.tool_calls.forEach((toolCall) => {
              const index = toolCall.index
              if (state.final_tool_calls[index] === undefined) {
                state.final_tool_calls[index] = {
                  ...toolCall,
                  function: { ...toolCall.function, arguments: '' },
                }

                // Dispatch tool detection event
                if (dispatch && toolCall.id && toolCall.function?.name) {
                  // Debug: Log tool call detection
                  logger.info('SSE processor: Tool call detected', {
                    toolId: toolCall.id,
                    toolUuid: toolCall.toolUuid,
                    toolName: toolCall.function.name,
                    toolIndex: toolCall.index,
                  })

                  dispatch({
                    type: 'TOOL_CALL_DETECTED',
                    payload: {
                      id: toolCall.id,
                      name: toolCall.function.name,
                      index: toolCall.index,
                      toolUuid: toolCall.toolUuid,
                      currentContent: state.content, // Pass current content to finalize assistant message
                    },
                  })
                  // Clear content after first tool call to prevent duplicates
                  state.content = ''
                }
              }

              // Simply accumulate arguments
              state.final_tool_calls[index].function.arguments +=
                toolCall.function.arguments
            })
          }
          if (
            ['stop', 'tool_calls'].includes(chunk.choices[0]?.finish_reason)
          ) {
            if (dispatch) {
              onDone(controller, state, dispatch, encoder)
              state.final_tool_calls = {}
            }
          }
          if (dispatch) {
            dispatch({
              type: 'STREAM_CHUNK_RECEIVED',
              payload: {
                timestamp: new Date().getTime(),
              },
            })
          }
        } catch (error) {
          logger.warn('Failed to parse SSE JSON data', {
            errorMessage:
              error instanceof Error ? error.message : String(error),
            rawData: jsonData,
          })
        }
      }
    }
  }
}
